import React from "react";
import { getLocalizedString } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from "@material-ui/core";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import PaperDraggable from "@icarius-common/paperDraggable";
import DialogTransition from "@icarius-common/dialogTransition";
import DateFnsUtils from "@date-io/date-fns";
import { dateLocale } from "@icarius-localization/strings";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import useHandleForm from "./useHandleForm";
import { createRatingAction, modifyRatingAction } from "@icarius-pages/ratings/actions";
import { useDispatch } from "react-redux";
import CustomNumberFormat from '@icarius-common/dynamicInputs/customNumberFormat';

const gridStyle = { height: 75, padding: "0px 10px" };

const CreateEditDialog = (props) => {

  const {
    open,
    isLoading,
    data,
    handleClose,
  } = props;

  const dispatch = useDispatch();

  const create = (dataToSend) => {
    dispatch(createRatingAction(dataToSend))
      .then((res) => res?.status === 200 && handleClose());
  }

  const modify = (dataToSend) => {
    dispatch(modifyRatingAction(dataToSend))
      .then((res) => res?.status === 200 && handleClose());
  }

  const openValidationError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
  }

  const {
    isChile,
    isCreate,
    formData,
    setFormValue,
    submit,
  } = useHandleForm(data, create, modify, openValidationError);

  if (isLoading) return null;

  return (
    <Dialog
      open={open}
      TransitionComponent={DialogTransition}
      PaperComponent={PaperDraggable}
      maxWidth={"md"}
      fullWidth={true}
    >
      <div className={"dialog-container"}>
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          {isCreate ? getLocalizedString("createIndex") : getLocalizedString("editIndex")}
          <DialogTitleDivider />
        </DialogTitle>
        <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
        <DialogContent>
          <Grid container item xs={12}>
            <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                <DatePicker
                  disabled={!isCreate}
                  required
                  style={{ marginTop: 0, marginBottom: 0, width: "100%" }}
                  label={getLocalizedString("processPeriod")}
                  cancelLabel={getLocalizedString("cancel")}
                  okLabel={getLocalizedString("ok")}
                  invalidDateMessage=""
                  format="yyyy/MM"
                  margin="normal"
                  value={formData['Período']}
                  onChange={(e) => setFormValue(e, 'Período')}
                  views={["year", "month"]}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
              <TextField
                InputProps={{ inputComponent: CustomNumberFormat }}
                fullWidth
                margin={"none"}
                label={'IPC'}
                value={formData['IPC']}
                onChange={(e) => setFormValue(e.target.value, "IPC")}
                inputProps={{ min: "0", step: "0.001", decimals: 3 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
              <TextField
                required
                InputProps={{ inputComponent: CustomNumberFormat }}
                fullWidth
                margin={"none"}
                value={formData['UF Último día mes anterior']}
                onChange={(e) => setFormValue(e.target.value, "UF Último día mes anterior")}
                label={getLocalizedString("ufLastDayPreviousMonth")}
                inputProps={{ min: "0", step: "0.001", decimals: 3 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
              <TextField
                required
                InputProps={{ inputComponent: CustomNumberFormat }}
                fullWidth
                margin={"none"}
                value={formData['UF Último día mes actual']}
                onChange={(e) => setFormValue(e.target.value, "UF Último día mes actual")}
                label={getLocalizedString("ufLastDayOfCurrentMonth")}
                inputProps={{ min: "0", step: "0.001", decimals: 3 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
              <TextField
                InputProps={{ inputComponent: CustomNumberFormat }}
                fullWidth
                margin={"none"}
                value={formData['UF del día 9']}
                onChange={(e) => setFormValue(e.target.value, "UF del día 9")}
                label={getLocalizedString("ufOfTheDay9")}
                inputProps={{ min: "0", step: "0.001", decimals: 3 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
              <TextField
                required
                InputProps={{ inputComponent: CustomNumberFormat }}
                fullWidth
                margin={"none"}
                label={'UTM'}
                value={formData['UTM']}
                onChange={(e) => setFormValue(e.target.value, "UTM")}
                inputProps={{ min: "0", step: "0.001", decimals: 3 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
              <TextField
                required
                InputProps={{ inputComponent: CustomNumberFormat }}
                fullWidth
                margin={"none"}
                value={formData['Tope imponible']}
                onChange={(e) => setFormValue(e.target.value, "Tope imponible")}
                label={getLocalizedString("taxBase")}
                inputProps={{ min: "0", step: "0.001", decimals: 3 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
              <TextField
                required
                InputProps={{ inputComponent: CustomNumberFormat }}
                fullWidth
                margin={"none"}
                value={formData['Tope imponible seguro cesantía']}
                onChange={(e) => setFormValue(e.target.value, "Tope imponible seguro cesantía")}
                label={getLocalizedString("unemploymentInsuranceTaxableLimit")}
                inputProps={{ min: "0", step: "0.001", decimals: 3 }}
              />
            </Grid>
            {
              isChile &&
              <>
                <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
                  <TextField
                    required
                    InputProps={{ inputComponent: CustomNumberFormat }}
                    fullWidth
                    margin={"none"}
                    value={formData['Porcentaje aporte FONASA']}
                    onChange={(e) => setFormValue(e.target.value, "Porcentaje aporte FONASA")}
                    label={getLocalizedString("fonasaContributionPercentage")}
                    inputProps={{ min: "0", step: "0.001", decimals: 3 }}
                  />
                </Grid>
                <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
                  <TextField
                    required
                    InputProps={{ inputComponent: CustomNumberFormat }}
                    fullWidth
                    margin={"none"}
                    value={formData['Porcentaje aporte CAJA']}
                    onChange={(e) => setFormValue(e.target.value, "Porcentaje aporte CAJA")}
                    label={getLocalizedString("cajaContributionPercentage")}
                    inputProps={{ min: "0", step: "0.001", decimals: 3 }}
                  />
                </Grid>
                <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
                  <TextField
                    required
                    InputProps={{ inputComponent: CustomNumberFormat }}
                    fullWidth
                    margin={"none"}
                    value={formData['Porcentaje rentabilidad protegida']}
                    onChange={(e) => setFormValue(e.target.value, "Porcentaje rentabilidad protegida")}
                    label={getLocalizedString("porcentajeRentabilidadProtegida")}
                    inputProps={{ min: "0", step: "0.001", decimals: 3 }}
                  />
                </Grid>
                <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
                  <TextField
                    required
                    InputProps={{ inputComponent: CustomNumberFormat }}
                    fullWidth
                    margin={"none"}
                    value={formData['Porcentaje Expectativa de vida']}
                    onChange={(e) => setFormValue(e.target.value, "Porcentaje Expectativa de vida")}
                    label={getLocalizedString("porcentajeExpectativaDeVida")}
                    inputProps={{ min: "0", step: "0.001", decimals: 3 }}
                  />
                </Grid>
                <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
                  <TextField
                    required
                    InputProps={{ inputComponent: CustomNumberFormat }}
                    fullWidth
                    margin={"none"}
                    value={formData['Porcentaje capitalización individual']}
                    onChange={(e) => setFormValue(e.target.value, "Porcentaje capitalización individual")}
                    label={getLocalizedString("porcentajeCapitalizaciónIndividual")}
                    inputProps={{ min: "0", step: "0.001", decimals: 3 }}
                  />
                </Grid>
              </>
            }
            <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
              <TextField
                required
                InputProps={{ inputComponent: CustomNumberFormat }}
                fullWidth
                margin={"none"}
                value={formData['Salario mínimo']}
                onChange={(e) => setFormValue(e.target.value, "Salario mínimo")}
                label={getLocalizedString("minimumWage")}
                inputProps={{ min: "0", step: "0.001", decimals: 3 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
              <TextField
                InputProps={{ inputComponent: CustomNumberFormat }}
                fullWidth
                margin={"none"}
                value={formData['Salario Grado 1-A']}
                onChange={(e) => setFormValue(e.target.value, "Salario Grado 1-A")}
                label={getLocalizedString("salaryGrade1a")}
                inputProps={{ min: "0", step: "0.001", decimals: 3 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
              <TextField
                InputProps={{ inputComponent: CustomNumberFormat }}
                fullWidth
                margin={"none"}
                value={formData['Salario no remunerado']}
                onChange={(e) => setFormValue(e.target.value, "Salario no remunerado")}
                label={getLocalizedString("unpaidSalary")}
                inputProps={{ min: "0", step: "0.001", decimals: 3 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
              <TextField
                InputProps={{ inputComponent: CustomNumberFormat }}
                fullWidth
                margin={"none"}
                value={formData['Salario vital']}
                onChange={(e) => setFormValue(e.target.value, "Salario vital")}
                label={getLocalizedString("vitalSalary")}
                inputProps={{ min: "0", step: "0.001", decimals: 3 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
              <TextField
                InputProps={{ inputComponent: CustomNumberFormat }}
                fullWidth
                margin={"none"}
                value={formData['Dólar']}
                onChange={(e) => setFormValue(e.target.value, "Dólar")}
                label={getLocalizedString("dollar")}
                inputProps={{ min: "0", step: "0.001", decimals: 3 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} md={6} style={gridStyle}>
              <TextField
                InputProps={{ inputComponent: CustomNumberFormat }}
                fullWidth
                margin={"none"}
                value={formData['Euro']}
                onChange={(e) => setFormValue(e.target.value, "Euro")}
                label={getLocalizedString("euro")}
                inputProps={{ min: "0", step: "0.001", decimals: 3 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <ButtonDialogAction onClick={handleClose} text={getLocalizedString("cancel")} />
          <ButtonDialogAction onClick={submit} isAccept text={getLocalizedString("accept")} />
        </DialogActions>
      </div>
    </Dialog>
  );
}

export default CreateEditDialog;
